import React, { useState } from 'react';
import './CreatorGrid.css';
import CreatorCard from './CreatorCard';
import CreatorModal from './CreatorModal';

function CreatorGrid({ 
  creators = [], 
  loading = false, 
  error = null,
  selectedCreators, 
  setSelectedCreators, 
  setAddToCampaignOpen, 
  activeTab, 
  shortlistCampaign, 
  onCreatorClick, 
  onAssignCreator, 
  shortlistedCreators, 
  onRemoveFromShortlist, 
  favouriteCreators = [], 
  isFavourite = () => false, 
  onToggleFavourite = () => {},
  isSelected = () => false,
  toggleCreatorSelection = () => {}
}) {
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCreatorForCampaign, setSelectedCreatorForCampaign] = useState(null);

  // Use only real creators from creator dashboard
  const allCreators = creators || [];

  const handleCardClick = (creator) => {
    if (onCreatorClick) {
      onCreatorClick(creator);
    } else {
      setSelectedCreator(creator);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCreator(null);
  };

  // Toggle selection for bulk add to campaign
  const handleToggleSelect = (creator) => {
    setSelectedCreators((prev) =>
      prev.some((c) => c.handle === creator.handle)
        ? prev.filter((c) => c.handle !== creator.handle)
        : [...prev, creator]
    );
  };

  // Open AddToCampaignModal for multiple creators
  const handleAddToCampaign = (creator) => {
    // If called from a card, add that creator to selection if not already selected
    if (creator && !selectedCreators.some((c) => c.handle === creator.handle)) {
      setSelectedCreators((prev) => [...prev, creator]);
    }
    setAddToCampaignOpen(true);
    setSelectedCreatorForCampaign(null); // Not needed for multi
  };

  // Determine which creators to display based on active tab
  const getDisplayCreators = () => {
    if (activeTab === 'favourite') {
      return favouriteCreators.map(c => ({
        ...c,
        image: c.image || c.profileImage || 'https://via.placeholder.com/200',
        name: c.name || c.handle || 'Unknown',
        age: c.age || '',
        niche: c.niche || '',
        hobbies: c.hobbies || '',
        bio: c.bio || '',
        profileImage: c.profileImage || c.image || 'https://via.placeholder.com/40',
      }));
    } else if (activeTab === 'shortlist') {
      return shortlistedCreators.map(c => ({
        ...c,
        image: c.image || c.profileImage || 'https://via.placeholder.com/200',
        name: c.name || c.handle || 'Unknown',
        age: c.age || '',
        niche: c.niche || '',
        hobbies: c.hobbies || '',
        bio: c.bio || '',
        profileImage: c.profileImage || c.image || 'https://via.placeholder.com/40',
      }));
    } else {
      // For explore and recent tabs, use real creators from creator dashboard
      return allCreators;
    }
  };

  const displayCreators = getDisplayCreators();

  // Show loading state
  if (loading && (activeTab === 'explore' || activeTab === 'recent')) {
    return (
      <div className="creator-grid">
        <div className="creator-grid-loading">
          <div className="loading-spinner"></div>
          <p>Loading creators...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && (activeTab === 'explore' || activeTab === 'recent')) {
    return (
      <div className="creator-grid">
        <div className="creator-grid-error">
          <p>Error loading creators: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-grid">
      {displayCreators.map((creator, index) => (
        <CreatorCard
          key={creator.id || index}
          creator={creator}
          imagePriority={index < 6}
          onClick={() => handleCardClick(creator)}
          onAddToCampaign={handleAddToCampaign}
          onToggleSelect={handleToggleSelect}
          isSelected={isSelected ? isSelected(creator.id) : selectedCreators.some((c) => c.handle === creator.handle)}
          actionLabel={activeTab === 'shortlist' ? 'Assign' : undefined}
          onAction={activeTab === 'shortlist' ? () => onAssignCreator && onAssignCreator(creator) : undefined}
          onRemove={activeTab === 'shortlist' ? () => onRemoveFromShortlist && onRemoveFromShortlist(creator) : undefined}
          isFavourite={isFavourite(creator)}
          onToggleFavourite={onToggleFavourite}
        />
      ))}
      {!onCreatorClick && modalOpen && selectedCreator && (
        <CreatorModal
          creator={selectedCreator}
          onClose={handleCloseModal}
          onAddToCampaign={handleAddToCampaign}
          onToggleSelect={handleToggleSelect}
          isSelected={selectedCreators.some((c) => c.handle === selectedCreator.handle)}
          actionLabel={activeTab === 'shortlist' ? 'Assign' : undefined}
          onAction={activeTab === 'shortlist' ? () => onAssignCreator && onAssignCreator(selectedCreator) : undefined}
          onRemove={activeTab === 'shortlist' ? () => onRemoveFromShortlist && onRemoveFromShortlist(selectedCreator) : undefined}
        />
      )}
    </div>
  );
}

export default CreatorGrid;

